import React from "react";
import { motion } from "framer-motion";
import { Badge } from "@/shared/components/ui/badge";
import { Progress } from "@/shared/components/ui/progress";
import { Activity, Zap, Clock, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";

const statusConfig = {
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  degraded: { icon: AlertTriangle, color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  inactive: { icon: XCircle, color: "bg-red-100 text-red-700", dot: "bg-red-500" },
  maintenance: { icon: Clock, color: "bg-slate-100 text-slate-700", dot: "bg-slate-500" },
};

export default function ProviderStatus({ providers = [] }) {
  const { t, language } = useLanguage();

  // Helper function to translate provider status
  const translateStatus = (status) => {
    if (!status) return t("na") || "N/A";
    const statusLower = String(status).toLowerCase().trim();
    const statusMap = {
      "active": t("active"),
      "inactive": t("inactive"),
      "degraded": t("degraded"),
      "maintenance": t("maintenance"),
    };
    return statusMap[statusLower] || status;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }}
      className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-slate-900">{t("provider")} {t("status")}</h3>
        <Badge variant="outline" className="gap-1">
          <Activity className="h-3 w-3" />
          {t("live")}
        </Badge>
      </div>
      
      <div className="space-y-4">
        {providers.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            {t("noProvidersConfigured")}
          </div>
        ) : (
          providers.map((provider, index) => {
            const statusKey = (provider.status || '').toLowerCase();
            const status = statusConfig[statusKey] || statusConfig.active;
            
            return (
              <motion.div
                key={provider.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * index }}
                className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${status.dot} animate-pulse`} />
                    <span className="font-medium text-slate-900">{getProviderName(provider, language)}</span>
                    <Badge variant="outline" className="text-xs">{provider.code}</Badge>
                  </div>
                  <Badge className={`${status.color} border-0 text-xs`}>
                    {translateStatus(provider.status)}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 mb-1">{t("successRate")}</p>
                    <div className="flex items-center gap-2">
                      <Progress value={provider.success_rate || 100} className="h-2" />
                      <span className="font-medium text-slate-700">{provider.success_rate || 100}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">{t("avgResponse")}</p>
                    <div className="flex items-center gap-1">
                      <Zap className="h-4 w-4 text-amber-500" />
                      <span className="font-medium text-slate-700">{provider.avg_response_time || 0}ms</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">{t("healthScore")}</p>
                    <div className="flex items-center gap-2">
                      <Progress value={provider.health_score || 100} className="h-2" />
                      <span className="font-medium text-slate-700">{provider.health_score || 100}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </motion.div>
  );
}